import React, { useState } from 'react';
import { Space, Tag, Modal, Tooltip } from 'antd';
import { EyeOutlined, FileAddOutlined } from '@ant-design/icons';
import moment from 'moment';
import { STUDY_DATETIME_FORMAT } from '../../../../utils/helper';
import type { Report } from '../../types';
import { getStatusStyle } from './statusStyles';
import { downloadDocumentPdf } from '../../../OnlyOfficeDocuments/documentsApi';
import PdfViewer from '../../../../components/PdfViewer';
import { Spin, message } from 'antd';

interface PriorReportsProps {
  reports: Report[];
  isSignedOff: boolean;
  onLoadAsTemplate: (report: Report) => void;
}

const PriorReports: React.FC<PriorReportsProps> = ({ reports, isSignedOff, onLoadAsTemplate }) => {
  const [viewingReport, setViewingReport] = useState<Report | null>(null);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // A doc-backed report is ALWAYS previewed by converting its .docx — that is the only
  // rendering that preserves formatting. `pr_report_html` must never win here: on an
  // OnlyOffice report it holds mammoth-extracted RAW TEXT written at sign-off for search
  // indexing (see submit-report-oodocs), not html, so rendering it drops the letterhead,
  // signature, tables and every line break. It is a fallback for legacy pre-OnlyOffice
  // reports only, which carry no document. Mirrors renderSignedReportPdf on the backend.
  const handleViewReport = async (report: any) => {
    setViewingReport(report);
    if (report.document?.doc_id) {
      setPdfLoading(true);
      try {
        const pdfBlob = await downloadDocumentPdf(report.document.doc_id);
        const buffer = await pdfBlob.arrayBuffer();
        setPdfData(buffer);
      } catch (err) {
        console.error('Error fetching PDF preview:', err);
        message.error('Failed to load document preview');
      } finally {
        setPdfLoading(false);
      }
    }
  };

  const handleCloseView = () => {
    setViewingReport(null);
    setPdfData(null);
  };

  // Ensure only the latest report per prior order is shown, irrespective of status
  const latestReports = React.useMemo(() => {
    const seen = new Set();
    const result: Report[] = [];
    for (const r of reports) {
      const orderId =(r as any)?.pr_pacs_ord_id || (r as any)?.pacs_ord_id;
      if (orderId) {
        if (!seen.has(orderId)) {
          seen.add(orderId);
          result.push(r);
        }
      } else {
        result.push(r);
      }
    }
    return result;
  }, [reports]);

  if (!latestReports.length) {
    return <div className="no-content-message">No prior reports available</div>;
  }

  return (
    <>
      {latestReports.map((record: any) => {
        const studyDate = record?.order?.po_req_time
          ? moment(record.order.po_req_time).format(STUDY_DATETIME_FORMAT)
          : (record.createdAt ? moment(record.createdAt).format(STUDY_DATETIME_FORMAT) : '-');
        // Blank body parts are common on HL7-first orders — don't render a bare "- - -".
        const studyLabel =
          [record?.order?.po_modality, record?.order?.po_body_part].filter(Boolean).join(' · ') || '—';
        const reportedBy = record.pr_reported_by || '—';

        return (
          <div
            key={record.pr_id}
            className="report-list-item"
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}
          >
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Line 1: status + study */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <Tag style={{ ...getStatusStyle(record.pr_status), margin: 0, fontSize: 11, lineHeight: '18px', flex: 'none' }}>
                  {record.pr_status?.replaceAll('_', ' ')}
                </Tag>
                <span
                  style={{
                    color: '#0F172A',
                    fontWeight: 500,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={studyLabel}
                >
                  {studyLabel}
                </span>
              </div>
              {/* Line 2: study date + reporter */}
              <div
                style={{
                  color: '#64748b',
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {studyDate} · {reportedBy}
              </div>
            </div>
            {/* Bare icons, not antd Buttons — a size="small" Button forces a 24px row */}
            <Space size={4} style={{ flex: 'none' }}>
              <Tooltip title="View Report">
                <EyeOutlined
                  onClick={() => handleViewReport(record)}
                  style={{ fontSize: '1rem', cursor: 'pointer', color: '#7D48B0' }}
                />
              </Tooltip>
              <Tooltip title="Load as Template">
                <FileAddOutlined
                  onClick={() => onLoadAsTemplate(record)}
                  style={{ fontSize: '1rem', cursor: 'pointer', color: '#7D48B0' }}
                />
              </Tooltip>
            </Space>
          </div>
        );
      })}
      <Modal
        title="View Prior Report"
        open={!!viewingReport}
        onCancel={handleCloseView}
        footer={null}
        width={1000}
      >
        <Spin spinning={pdfLoading} tip="Loading report preview...">
          {pdfData ? (
            <div style={{ height: '80vh', overflowY: 'auto' }}>
              <PdfViewer
                pdfArrayBuffer={pdfData}
                patientData={{ patient: viewingReport?.order?.patient }}
                mimeType="application/pdf"
                hideCopyButton
                hideWhatsappButton
              />
            </div>
          ) : /* Legacy pre-OnlyOffice report (no document) — real html, safe to render. */
          !viewingReport?.document?.doc_id && viewingReport?.pr_report_html ? (
            <div
              className="report-preview-content"
              dangerouslySetInnerHTML={{ __html: viewingReport.pr_report_html }}
              style={{ padding: '20px', background: '#fff', border: '1px solid #d9d9d9', minHeight: '400px', maxHeight: '70vh', overflowY: 'auto' }}
            />
          ) : (
            !pdfLoading && <div className="no-content-message">No report content available</div>
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default PriorReports;
